import { ArrowRight, ClipboardList, PlaneTakeoff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { ProductCard, RegionCard, RegulatedNote, regionOfferCount } from "../components/Cards";
import { usePageHeader } from "../components/shell/PageHeaderContext";
import { TripMilestones } from "../components/TripMilestones";
import { money } from "../lib/format";
import { useDiscover, useMyRequests, useProducts, useTrip, useTrips, useWallets } from "../lib/queries";
import { REQUEST_STATUS_LABELS, REQUEST_STATUS_TONES, mxn } from "../lib/reservations";
import { CustomTripInline } from "../components/trips/CustomTripPrompt";
import { TripProductCard } from "../components/trips/TripProductCard";

/** Inicio: a summary of the customer's savings, their trip terms, the products, and the regions. */
export function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const first = user?.fullName?.split(" ")[0] ?? "";
  usePageHeader(`Hola, ${first}`, "Ahorra, aparta y viaja a tu ritmo.");
  const wallets = useWallets();
  const products = useProducts();
  const regions = useDiscover();
  const trip = useTrip();
  const trips = useTrips();
  const requests = useMyRequests();

  const list = wallets.data ?? [];
  const total = list.reduce((sum, wallet) => sum + wallet.balance.available, 0);
  const earned = list.reduce((sum, wallet) => sum + wallet.balance.accruedInterest, 0);
  const active = list.filter((wallet) => wallet.status === "active").length;
  const libre = list.find((wallet) => wallet.product === "libre");

  return (
    <div className="page-container">
      <section className="balance-strip" aria-label="Balance total">
        <div className="balance-main">
          <span className="hero-eyebrow">Balance total</span>
          <strong translate="no">{money.format(total)}</strong>
        </div>
        <div className="balance-stat"><span>Rendimiento ganado</span><strong className="hi" translate="no">+{money.format(earned)}</strong></div>
        <div className="balance-stat"><span>Wallets activas</span><strong>{active}</strong></div>
      </section>

      {trip.data && <TripMilestones trip={trip.data} saved={libre?.balance.available ?? 0} />}

      {/* The customer's own trips: Apartados and open requests, so Inicio leads back into the flow. */}
      {(libre?.apartados.length || requests.data?.requests.some((request) => request.statusGroup !== "closed")) ? (
        <>
          <div className="section-heading">
            <div><span className="page-eyebrow">Tus viajes</span><h2>Tu próximo viaje</h2></div>
            <button type="button" className="link-button" onClick={() => navigate("/solicitudes")}>Mis solicitudes <ArrowRight size={14} aria-hidden="true" /></button>
          </div>
          <div className="next-trip-grid">
            {(libre?.apartados ?? []).map((apartado) => (
              <button key={apartado.id} type="button" className="next-trip-card" onClick={() => navigate(`/apartados/${apartado.id}`)}>
                <span className="benefit-icon"><PlaneTakeoff size={17} aria-hidden="true" /></span>
                <div><strong>Apartado para {apartado.tripName}</strong><span translate="no">{mxn(apartado.balance)} apartados · meta {mxn(apartado.goalAmount)}</span></div>
                <ArrowRight size={16} aria-hidden="true" />
              </button>
            ))}
            {(requests.data?.requests ?? []).filter((request) => request.statusGroup !== "closed" && !request.apartado).slice(0, 3).map((request) => (
              <button key={request.id} type="button" className="next-trip-card" onClick={() => navigate(`/solicitudes/${request.id}`)}>
                <span className="benefit-icon"><ClipboardList size={17} aria-hidden="true" /></span>
                <div><strong>{request.trip.name}</strong><span><span className={`status-pill ${REQUEST_STATUS_TONES[request.status]}`}>{REQUEST_STATUS_LABELS[request.status]}</span> <span translate="no">{request.folio}</span></span></div>
                <ArrowRight size={16} aria-hidden="true" />
              </button>
            ))}
          </div>
        </>
      ) : null}

      <div className="section-heading">
        <div><span className="page-eyebrow">Finanzas</span><h2>Ahorra o financia tu viaje como nunca antes</h2></div>
        <button type="button" className="link-button" onClick={() => navigate("/wallet")}>Ver todo <ArrowRight size={14} aria-hidden="true" /></button>
      </div>
      <div className="product-grid">
        {(products.data?.products ?? []).map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            onClick={() => navigate(product.id === "libre" ? (libre ? "/wallet/libre" : "/wallet/libre/nueva") : `/wallet/${product.id}`)}
            meta={product.id === "libre" && libre ? <>Ahorrado <strong translate="no">{money.format(libre.balance.available)}</strong></> : undefined}
          />
        ))}
      </div>

      {(trips.data?.length ?? 0) > 0 && (
        <>
          <div className="section-heading">
            <div><span className="page-eyebrow">Viajes</span><h2>Viajes listos para reservar</h2></div>
            <button type="button" className="link-button" onClick={() => navigate("/explorar")}>Ver todos <ArrowRight size={14} aria-hidden="true" /></button>
          </div>
          <div className="trip-card-grid">
            {(trips.data ?? []).slice(0, 3).map((item) => (
              <TripProductCard key={item.id} trip={item} placement="home" onClick={() => navigate(`/viajes/${item.slug}`)} />
            ))}
          </div>
        </>
      )}

      <div className="section-heading">
        <div><span className="page-eyebrow">Viajes</span><h2>Elige tu próximo destino</h2></div>
        <button type="button" className="link-button" onClick={() => navigate("/explorar")}>Ver todas <ArrowRight size={14} aria-hidden="true" /></button>
      </div>
      <div className="region-grid">
        {(regions.data ?? []).slice(0, 2).map((region) => (
          <RegionCard key={region.id} region={region} count={regionOfferCount(region, trips.data ?? [])} onClick={() => navigate(`/explorar/${region.slug}`)} />
        ))}
      </div>
      <CustomTripInline
        placement="home"
        title="¿Aún no sabes a dónde ir?"
        text="Cuéntanos qué te gustaría vivir y un especialista te recomienda destinos y arma tu viaje a la medida."
        cta="Pedir recomendaciones"
        prefill={{ recommend: true }}
      />
      <RegulatedNote />
    </div>
  );
}
